import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Terminal, Search } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENDPOINTS = ["intent", "semantic", "source", "graph", "resolve", "context"];

export default function CognitiveEndpoint({ kind }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "artificial intelligence");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/${kind}?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    fetchData(searchParams.get("q") || "artificial intelligence");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, searchParams]);

  const run = () => setSearchParams({ q: query });

  return (
    <div className="min-h-screen bg-[#0A1A2F]">
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between gap-4 mb-10">
          <Logo />
          <Link
            to="/"
            data-testid="endpoint-back"
            className="flex items-center gap-1.5 font-mono text-[13px] text-[#A7B4C2] hover:text-[#4BE3FF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <Terminal className="w-5 h-5 text-[#4BE3FF]" />
          <span className="font-mono text-[12px] uppercase tracking-[0.3em] text-[#4BE3FF]">
            Cognitive Endpoint
          </span>
        </div>
        <h1
          data-testid="endpoint-title"
          className="font-display font-bold text-4xl md:text-5xl tracking-tight text-white"
        >
          /{kind}
        </h1>
        <p className="font-mono text-[13px] text-[#A7B4C2] mt-3">
          GET <span className="text-[#4BE3FF]">{API}/{kind}?q=…</span>
        </p>

        {/* Query bar */}
        <div className="mt-8 glass rounded-2xl p-2 flex items-center gap-2 cyan-glow">
          <Search className="w-5 h-5 text-[#4BE3FF] ml-3" />
          <input
            data-testid="endpoint-query-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            className="flex-1 bg-transparent outline-none font-mono text-[14px] text-white placeholder:text-[#A7B4C2]/60 py-3"
            placeholder="Query…"
          />
          <button
            data-testid="endpoint-run"
            onClick={run}
            className="rounded-xl bg-[#1E6FFF] hover:bg-[#4BE3FF] hover:text-[#0A1A2F] text-white font-mono text-sm px-5 py-3 transition-colors duration-300"
          >
            Run
          </button>
        </div>

        {/* Endpoint switcher */}
        <div className="mt-6 flex flex-wrap gap-2">
          {ENDPOINTS.map((e) => (
            <Link
              key={e}
              to={`/${e}?q=${encodeURIComponent(query)}`}
              data-testid={`endpoint-link-${e}`}
              className={`font-mono text-[12px] rounded-full px-3 py-1.5 border transition-colors duration-300 ${
                e === kind
                  ? "border-[#4BE3FF]/60 bg-[#4BE3FF]/10 text-[#4BE3FF]"
                  : "border-white/10 text-[#A7B4C2] hover:border-[#4BE3FF]/40 hover:text-[#4BE3FF]"
              }`}
            >
              /{e}
            </Link>
          ))}
        </div>

        {/* Response */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-8 rounded-2xl border border-[#A7B4C2]/15 bg-[#05101f] overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <span className="font-mono text-[12px] text-[#A7B4C2]">response.json</span>
            <span className="font-mono text-[11px] flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  loading ? "bg-[#A7B4C2] animate-pulse" : error ? "bg-red-400" : "bg-[#4BE3FF]"
                }`}
              />
              <span className="text-[#A7B4C2]">
                {loading ? "reasoning…" : error ? `error ${error}` : "200 OK"}
              </span>
            </span>
          </div>
          <pre
            data-testid="endpoint-response"
            className="p-6 overflow-x-auto font-mono text-[13px] leading-relaxed text-[#4BE3FF] whitespace-pre-wrap break-words min-h-[160px]"
          >
{loading ? "// Rexxengine is reasoning over your query…" : JSON.stringify(data ?? { error }, null, 2)}
          </pre>
        </motion.div>
      </div>
    </div>
  );
}
